<template>
  <v-layout align-center>
    <v-flex xs12 sm10 md8 lg6>
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="$apollo.loading">
            <spinner />
          </div>
          <div v-else class="mobile-button-container">
            <div class="mobile-button hide">
              <main-button depressed @click="createFieldDialogOpen = true"> Add field </main-button>
            </div>
            <div class="title-screen">
              <span>Attendee fields</span>
            </div>
            <div class="title-description attendee-table-description mb-32">
              Create new attendee fields. Choose the order in which they will appear. Fields can be
              of different types and you can name it like you want.
            </div>
            <div class="attendee-field-subtitle">
              <main-button depressed class="desktop-button" @click="createFieldDialogOpen = true">
                <span class="add-symbol">+</span>Add field
              </main-button>
              <main-button v-if="!rearrange" depressed flat @click="rearranging()">
                REARRANGE
              </main-button>
              <main-button v-else depressed flat outline @click="rearranging()"> DONE </main-button>
            </div>
            <p class="page-description" style="margin-top: 1rem">
              Labels for First name, Last name and Email are editable on the Translate page.
            </p>
            <draggable
              v-if="rearrange"
              v-model="list"
              :v-bind="animation"
              group="id"
              @change="save"
            >
              <transition-group type="transition" :name="'flip-list'">
                <ul v-for="field in list" :key="field.name" style="padding-left: 0">
                  <v-list-tile class="extend-height tile">
                    <v-list-tile-content class="drag-content">
                      <v-icon :size="19" class="drag-indicator">drag_indicator</v-icon>
                      <v-list-tile-title>
                        {{ field.name }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </ul>
              </transition-group>
            </draggable>

            <transition-group v-else type="transition" :name="'flip-list'">
              <ul v-for="field in list" :key="field.name" style="padding-left: 0">
                <v-list-tile
                  class="attendee-field-editable-tile"
                  :disabled="disabledFieldUpdate.includes(field.name)"
                  @click="openUpdateDialog(field)"
                >
                  <v-list-tile-content class="drag-content">
                    <v-list-tile-title
                      :class="disabledFieldUpdate.includes(field.name) && 'disabled-field'"
                    >
                      {{ field.name }}
                      <img
                        v-if="disabledFieldUpdate.includes(field.name)"
                        alt="lock-icon"
                        src="/assets/lock.svg"
                        s
                      >
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </ul>
            </transition-group>
            <div>
              <attendee-field-dialog
                :show.sync="createFieldDialogOpen"
                :fields="list"
                :attendees-exist="attendeesExist"
                action="add"
                @submit="createAttendeeField"
              />
              <attendee-field-dialog
                :field="selectedField"
                :fields="list"
                :show.sync="updateFieldDialogOpen"
                :attendees-exist="attendeesExist"
                action="update"
                @submit="updateAttendeeField"
                @delete="deleteAttendeeField"
              />
            </div>
          </div>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import draggable from 'vuedraggable'
import { v4 } from 'uuid'
import { ATTENDEE_FIELDS } from '../../graphql/event.graphql'
import { saveAttendeeFields } from '../../http'
import AttendeeFieldDialog from './attendee-field-dialog.vue'
import { objectToArray, getVisibleFields, setVisibleFields } from '../../utils'
import {
  listOrderComparator,
  findMaxOrderReducer,
  listToFieldsReducer,
} from '../accreditations/attendee-fields'
import { GET_EVENT_DETAILS } from '../../graphql/event.graphql'
import { difference } from 'lodash'

export default {
  name: 'attendee-fields-container',
  components: {
    draggable,
    AttendeeFieldDialog,
  },
  props: {
    eventId: String,
  },
  data() {
    return {
      drag: false,
      fields: null,
      disabledFieldUpdate: ['First name', 'Last name', 'Email'],
      list: [],
      createFieldDialogOpen: false,
      updateFieldDialogOpen: false,
      selectedField: {},
      attendeesExist: false,
      rearrange: false,
    }
  },
  computed: {
    maxOrder() {
      return this.list.reduce(findMaxOrderReducer, 0)
    },
    animation() {
      return {
        animation: 0,
        group: 'description',
        ghostClass: 'ghost',
      }
    },
  },
  methods: {
    async createAttendeeField(field) {
      this.list.push({
        ...field,
        id: v4(),
        order: this.maxOrder + 1,
        deletable: true,
      })
      const visibleFields = getVisibleFields(this.eventId)
      if (visibleFields.length < 10) {
        visibleFields.push(field.name)
        setVisibleFields(visibleFields, this.eventId)
      }
      await this.save()
    },
    async updateAttendeeField(field) {
      const index = this.list.findIndex(list => list.id === field.id)
      this.$set(this.list, index, field)
      setVisibleFields(
        this.list.map(f => f.name),
        this.eventId,
      )
      await this.save()
    },
    async deleteAttendeeField(id) {
      const index = this.list.findIndex(list => list.id === id)
      this.$delete(this.list, index)
      setVisibleFields(
        this.list.map(f => f.name),
        this.eventId,
      )

      await this.save()
    },
    orderList(list) {
      return list.sort(listOrderComparator)
    },
    mapFieldsToList(fields) {
      return objectToArray(fields, 'name')
    },
    mapListToFields() {
      return this.list.reduce(listToFieldsReducer, {})
    },
    listObjetToList() {
      return this.list.map(function (item) {
        return item['name']
      })
    },
    openUpdateDialog(field) {
      const { component, validations, type, items, fieldID, ...rest } = field
      this.selectedField = {
        ...rest,
        value: {
          fieldID,
          component,
          validations,
          type,
          items,
        },
      }
      this.updateFieldDialogOpen = true
    },
    async save() {
      const attendeeFields = this.mapListToFields()

      return saveAttendeeFields({
        eventId: this.eventId,
        attendeeFields,
      })
    },
    rearranging() {
      this.rearrange = !this.rearrange
    },
    fieldsWithSplitter() {
      const invisibleFields = difference(this.allFields, this.visibleFields.slice(0, 10))

      return [...this.visibleFields, 'splitter-divider', ...invisibleFields]
    },
  },
  onFieldsChanged(visibleFields) {
    this.$emit('update:visibleFields', visibleFields)
  },
  apollo: {
    getSortedAttendeeFields: {
      query: ATTENDEE_FIELDS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      result({
        data: {
          getEvent: { attendeeFields },
        },
        loading,
      }) {
        if (!loading) {
          if (!attendeeFields) return (this.list = [])
          this.list = this.orderList(this.mapFieldsToList(attendeeFields))
        }
      },
    },
    getEventAttendees: {
      query: GET_EVENT_DETAILS,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.attendeesExist = !!data.getAccreditationCount
        }
      },
    },
  },
}
</script>

<style>
.v-list__tile--link {
  border-radius: 8px;
}
.extend-height > div {
  height: 40px;
  padding: 0;
}
.attendee-field-editable-tile > a {
  height: 40px;
}
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.tile:hover {
  cursor: pointer;
}
.tile {
  background: rgba(2, 119, 189, 0.1);
  border-radius: 8px;
  margin-top: 8px;
  align-items: center;
  flex-direction: row;
  padding-left: 0px;
}
.attendee-field-name {
  font-size: 22px;
  line-height: 26px;
  margin-bottom: 0;
}
.attendee-field-subtitle {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-left: 0px;
  margin-bottom: 1.5rem;
}
.drag-content {
  flex-direction: row !important;
  align-items: center !important;
  padding-left: 0;
}
.drag-indicator {
  cursor: pointer;
  margin-right: 8px;
  margin-left: 8px;
  color: #0277bd !important;
  padding: 0;
}
.attendee-field-editable-tile {
  background: #f5f5f5;
  border-radius: 8px;
  margin-top: 8px;
  align-items: center;
  height: 40px;
}
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}
.disabled-field {
  color: #d9d9d9;
  display: flex;
  justify-content: space-between;
}
.add-symbol {
  font-size: 1.875rem;
  margin-right: 0.8125rem;
  font-weight: 200;
}
@media screen and (max-width: 800px) {
  .mobile-button-container {
    margin-top: 0.875rem !important;
  }
}
</style>
